/* Favoris — Scoop by Coralie (V18 lot 3)

   Enregistre localement les produits mis de côté par la cliente. Aucune
   application, aucun service externe, aucune donnée personnelle : uniquement
   `localStorage`, sur son appareil.

   Ce que cette fonction ne fait pas, et qu'il ne faut donc pas promettre :
   - aucune synchronisation entre appareils ou navigateurs ;
   - aucun lien avec le compte client ;
   - aucune persistance si la cliente efface les données du site.
   Le libellé de l'interface le dit explicitement.

   Un favori n'a jamais d'effet sur le panier : cliquer un cœur n'ajoute ni ne
   retire quoi que ce soit d'une commande.
*/
(() => {
  const CLE = 'sbc_wishlist_v1';

  const lire = () => {
    try {
      const brut = window.localStorage.getItem(CLE);
      const liste = brut ? JSON.parse(brut) : [];
      return Array.isArray(liste) ? liste.filter((h) => typeof h === 'string') : [];
    } catch (_) { return []; }
  };

  const ecrire = (liste) => {
    try { window.localStorage.setItem(CLE, JSON.stringify([...new Set(liste)])); }
    catch (_) { /* mode privé ou quota : le favori ne persiste pas, rien ne casse */ }
    /* Lot 104 — la liste vient de changer, et ce module est le seul à
       l'écrire : il l'annonce. La page « Mes favoris » écoutait auparavant
       tous les clics du document puis attendait 0 ms en pariant que
       l'écriture avait eu lieu entre-temps. */
    document.dispatchEvent(new CustomEvent('sbc:favoris-modifies'));
  };

  const contient = (handle) => lire().indexOf(handle) !== -1;

  const basculer = (handle) => {
    const liste = lire();
    const i = liste.indexOf(handle);
    if (i === -1) liste.push(handle); else liste.splice(i, 1);
    ecrire(liste);
    return i === -1;
  };

  /* Les cartes produits classiques arrivent déjà avec leur bouton en Liquid.
     Les cartes Scoops de la home utilisent un composant différent : on les
     raccorde ici au même système en se basant sur leur URL produit. Cette
     préparation est idempotente et fonctionne aussi après un rendu de section
     dans l'éditeur de thème. */
  const preparerScoops = () => {
    document.querySelectorAll('.sbc-scoop-card').forEach((carte) => {
      const lien = carte.querySelector('a[href*="/products/"]');
      if (!lien) return;

      let handle = '';
      try {
        const chemin = new URL(lien.href, window.location.origin).pathname;
        const correspondance = chemin.match(/\/products\/([^/?#]+)/);
        if (correspondance && correspondance[1]) handle = decodeURIComponent(correspondance[1]);
      } catch (_) { return; }
      if (!handle) return;

      carte.setAttribute('data-sbc-carte-handle', handle);
      if (carte.querySelector('[data-sbc-favori]')) return;

      const titre = (carte.querySelector('h3')?.textContent || 'ce Scoop').trim();
      const bouton = document.createElement('button');
      bouton.type = 'button';
      bouton.className = 'sbc-favori';
      bouton.setAttribute('data-sbc-favori', handle);
      bouton.setAttribute('data-sbc-favori-titre', titre);
      bouton.setAttribute('aria-pressed', 'false');
      bouton.setAttribute('aria-label', `Ajouter ${titre} à vos favoris`);
      bouton.innerHTML = '<span class="sbc-favori__icone" aria-hidden="true">♥</span>';
      const visuel = carte.querySelector('.sbc-scoop-card__visuel');
      if (visuel) {
        visuel.appendChild(bouton);
      } else {
        carte.insertBefore(bouton, carte.firstChild);
      }
    });
  };

  /* Peinture. Le module reflète l'état enregistré sur tous les boutons déjà
     présents et sur ceux ajoutés aux cartes Scoops. */
  const peindre = () => {
    preparerScoops();
    const favoris = lire();
    document.querySelectorAll('[data-sbc-favori]').forEach((bouton) => {
      const handle = bouton.getAttribute('data-sbc-favori');
      const actif = favoris.indexOf(handle) !== -1;
      bouton.setAttribute('aria-pressed', actif ? 'true' : 'false');
      bouton.setAttribute('aria-label', actif
        ? `Retirer ${bouton.dataset.sbcFavoriTitre || 'ce produit'} de vos favoris`
        : `Ajouter ${bouton.dataset.sbcFavoriTitre || 'ce produit'} à vos favoris`);
    });
    document.querySelectorAll('[data-sbc-favoris-compte]').forEach((n) => {
      n.textContent = favoris.length ? ` (${favoris.length})` : '';
    });
    document.querySelectorAll('[data-sbc-favoris-filtre]').forEach((n) => {
      n.hidden = favoris.length === 0;
    });
    appliquerFiltre();
  };

  /* Filtre « Mes favoris ». Il n'ouvre pas une page séparée : il masque les
     cartes non retenues dans la grille déjà affichée. Un cœur mène donc
     toujours quelque part, y compris hors ligne. */
  let filtreActif = false;

  const appliquerFiltre = () => {
    const favoris = lire();
    document.querySelectorAll('[data-sbc-carte-handle]').forEach((carte) => {
      const handle = carte.getAttribute('data-sbc-carte-handle');
      const garder = !filtreActif || favoris.indexOf(handle) !== -1;
      carte.hidden = !garder;
    });
    document.querySelectorAll('[data-sbc-favoris-vide]').forEach((n) => {
      n.hidden = !(filtreActif && favoris.length === 0);
    });
    document.querySelectorAll('[data-sbc-favoris-filtre]').forEach((n) => {
      n.setAttribute('aria-pressed', filtreActif ? 'true' : 'false');
      n.classList.toggle('is-active', filtreActif);
    });
  };

  document.addEventListener('click', (e) => {
    const bouton = e.target.closest('[data-sbc-favori]');
    if (bouton) {
      /* La carte entière est un lien : sans cela, un clic sur le cœur
         ouvrirait la fiche produit. */
      e.preventDefault();
      e.stopPropagation();
      const handle = bouton.getAttribute('data-sbc-favori');
      const ajoute = basculer(handle);
      peindre();
      const annonce = document.querySelector('[data-sbc-favoris-annonce]');
      if (annonce) {
        annonce.textContent = ajoute
          ? `${bouton.dataset.sbcFavoriTitre || 'Produit'} ajouté à vos favoris.`
          : `${bouton.dataset.sbcFavoriTitre || 'Produit'} retiré de vos favoris.`;
      }
      return;
    }
    const filtre = e.target.closest('[data-sbc-favoris-filtre]');
    if (filtre) {
      e.preventDefault();
      filtreActif = !filtreActif;
      appliquerFiltre();
    }
  });

  document.addEventListener('sbc:cart-updated', peindre);
  document.addEventListener('shopify:section:load', peindre);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', peindre, { once: true });
  } else {
    peindre();
  }

  window.sbcFavoris = { lire, contient, peindre };
})();
